import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import {
  listNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  Notification,
} from '../services/api';

interface NotificationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
  isTenantAdmin?: boolean;
}

const NotificationDrawer: React.FC<NotificationDrawerProps> = ({ isOpen, onClose, userId, isTenantAdmin = false }) => {
  const { theme } = useTheme();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const [markingRead, setMarkingRead] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Reset to page 1 when drawer opens or userId/isTenantAdmin changes
      setPage(1);
      setHasMore(true);
      fetchNotifications(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId, isTenantAdmin]);

  // Infinite scroll handler
  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement || !isOpen) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = contentElement;
      // Load more when user scrolls within 100px of the bottom
      if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loading) {
        const nextPage = page + 1;
        setPage(nextPage);
        // Fetch next page
        fetchNotifications(nextPage);
      }
    };

    contentElement.addEventListener('scroll', handleScroll);
    return () => {
      contentElement.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasMore, loading, page]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        // Restore scroll position when drawer closes
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const fetchNotifications = async (pageToFetch?: number) => {
    const currentPage = pageToFetch !== undefined ? pageToFetch : page;
    setLoading(true);
    try {
      // For tenant admins, don't pass userId to see all notifications in their tenant
      // For regular users, pass their userId
      const response = await listNotifications(currentPage, pageSize, undefined, undefined, isTenantAdmin ? undefined : userId);
      if (response.data.success) {
        // Sort by createdAt descending (newest first) to ensure latest notifications are on top
        const sortedData = [...response.data.data].sort((a, b) => {
          const timeA = a.metadata?.createdAt || 0;
          const timeB = b.metadata?.createdAt || 0;
          return timeB - timeA; // Descending order (newest first)
        });
        
        if (currentPage === 1) {
          setNotifications(sortedData);
        } else {
          setNotifications((prev) => {
            // Combine and sort all notifications to ensure proper order
            const combined = [...prev, ...sortedData];
            return combined.sort((a, b) => {
              const timeA = a.metadata?.createdAt || 0;
              const timeB = b.metadata?.createdAt || 0;
              return timeB - timeA; // Descending order (newest first)
            });
          });
        }
        const pagination = response.data.pagination;
        setHasMore(pagination.currentPage < pagination.totalPages);
        if (pageToFetch !== undefined) {
          setPage(pageToFetch);
        }
      }
    } catch (error: any) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    setMarkingRead(notificationId);
    try {
      await markNotificationAsRead(notificationId);
      const now = Date.now();
      setNotifications((prev) =>
        prev.map((notif) => {
          if (notif._id === notificationId) {
            return {
              ...notif,
              spec: {
                ...notif.spec,
                read: true,
                readAt: now,
              },
            };
          }
          return notif;
        })
      );
      // Trigger a refresh of unread count in parent
      if (window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('notificationRead'));
      }
    } catch (error: any) {
      console.error('Error marking notification as read:', error);
    } finally {
      setMarkingRead(null);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      // For tenant admins, don't pass userId to mark all notifications in tenant as read
      // For regular users, pass their userId
      await markAllNotificationsAsRead(isTenantAdmin ? undefined : userId);
      const now = Date.now();
      setNotifications((prev) =>
        prev.map((notif) => ({
          ...notif,
          spec: {
            ...notif.spec,
            read: true,
            readAt: now,
          },
        }))
      );
      // Trigger a refresh of unread count in parent
      if (window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('notificationRead'));
      }
    } catch (error: any) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleRefresh = () => {
    fetchNotifications(1);
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return '#ef4444';
      case 'high':
        return '#f59e0b';
      case 'medium':
        return '#3b82f6';
      case 'low':
        return '#6b7280';
      default:
        return '#6b7280';
    }
  };

  // Calculate unread count
  const unreadCount = notifications.filter((n) => !n.spec.read).length;

  if (!isOpen) return null;

  return (
    <>
      <div className="notification-drawer-overlay" onClick={onClose} />
      <div className={`notification-drawer ${theme === 'dark' ? 'dark' : 'light'}`}>
        <div className="notification-drawer-header">
          <h3>Notifications</h3>
          <div className="notification-drawer-actions">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button"
              title="Refresh list"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            {unreadCount > 0 && (
              <button
                className="btn-mark-all-read"
                onClick={handleMarkAllAsRead}
                title="Mark all as read"
              >
                Mark all as read
              </button>
            )}
            <button className="btn-close-drawer" onClick={onClose} title="Close">
              ✕
            </button>
          </div>
        </div>
        <div className="notification-drawer-content" ref={contentRef}>
          {loading && notifications.length === 0 ? (
            <div className="notification-loading">Loading notifications...</div>
          ) : notifications.length === 0 ? (
            <div className="notification-empty">No notifications</div>
          ) : (
            <>
              {notifications.map((notification) => {
                const isUnread = !notification.spec.read;
                
                return (
                <div
                  key={notification._id}
                  className={`notification-item ${isUnread ? 'unread' : 'read'}`}
                  onClick={() => isUnread && handleMarkAsRead(notification._id)}
                >
                  <div className="notification-item-header">
                    <div className="notification-item-title-row">
                      <h4 className="notification-title">{notification.spec.title}</h4>
                      {isUnread && (
                        <span
                          className="notification-priority-dot"
                          style={{ backgroundColor: getPriorityColor(notification.spec.priority) }}
                        />
                      )}
                    </div>
                    <span className="notification-time">
                      {formatTime(notification.metadata.createdAt)}
                    </span>
                  </div>
                  <p className="notification-body">{notification.spec.body}</p>
                  {notification.spec.data?.bookingId && notification.spec.type !== 'booking_assigned' && (
                    <div className="notification-extra-info">
                      {notification.spec.data.bookingName && (
                        <span className="notification-booking-name">
                          {notification.spec.data.bookingName}
                        </span>
                      )}
                      {notification.spec.data.patientName && (
                        <span className="notification-patient-name">
                          Patient: {notification.spec.data.patientName}
                        </span>
                      )}
                    </div>
                  )}
                  {markingRead === notification._id && (
                    <div className="notification-marking-read">Marking as read...</div>
                  )}
                </div>
                );
              })}
              {loading && notifications.length > 0 && (
                <div className="notification-loading" style={{ padding: '16px', textAlign: 'center' }}>
                  Loading more notifications...
                </div>
              )}
              {!hasMore && notifications.length > 0 && (
                <div className="notification-empty" style={{ padding: '16px', textAlign: 'center', opacity: 0.6 }}>
                  No More Notifications
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationDrawer;

